/**
 * BCP-47 locale negotiation for icon aria-labels.
 *
 * The label tables (`ICON_LABELS` for built-in concepts,
 * `extendedRegistry[c].labels` for consumer-extended) carry a small
 * set of locale keys. When the caller requests `'de'` or `'pt-BR'`
 * the resolver walks a tagged chain:
 *
 *   1. exact match (`'pt-BR'`)
 *   2. primary subtag (`'pt'`)
 *   3. caller-supplied custom fallback (e.g. `'tr'` in TR-first products)
 *   4. canonical built-in (`'en'`) — always present on built-in vocab
 *
 * Empty / undefined labels are skipped so partial localisations don't
 * starve later tiers.
 */

export type LocaleLabels = Record<string, string | undefined>

/**
 * Resolve a single label string for a request locale, falling through
 * BCP-47 subtag chain and a caller-supplied fallback locale.
 *
 * Returns `undefined` only when no tier has any label at all (which
 * shouldn't happen for built-in concepts since `en` is always set).
 */
export function resolveLocalisedLabel(
  labels: LocaleLabels | undefined,
  locale: string,
  fallbackLocale: string = 'en',
): string | undefined {
  if (!labels) return undefined
  const tried = new Set<string>()
  for (const tag of expandBcp47(locale)) {
    if (tried.has(tag)) continue
    tried.add(tag)
    const value = labels[tag]
    if (value !== undefined && value !== '') return value
  }
  // Caller-supplied middle tier (e.g. region-default 'tr' before
  // dropping to canonical 'en'). Only applied when distinct from
  // already-tried tags.
  if (fallbackLocale && !tried.has(fallbackLocale)) {
    const value = labels[fallbackLocale]
    if (value !== undefined && value !== '') return value
  }
  // Final tier: canonical 'en'. Built-in vocabulary always carries it.
  if (!tried.has('en')) {
    const value = labels.en
    if (value !== undefined && value !== '') return value
  }
  return undefined
}

/**
 * Expand a BCP-47 tag into a fallback chain.
 *
 * `'pt-BR'`  → `['pt-BR', 'pt']`
 * `'zh-Hant-HK'` → `['zh-Hant-HK', 'zh-Hant', 'zh']`
 * `'en'`     → `['en']`
 *
 * The chain is ordered from most-specific to least-specific so the
 * caller's pickiest match wins.
 */
export function expandBcp47(locale: string): string[] {
  const lower = locale.toLowerCase()
  // Normalise BCP-47 case conventions for the per-segment expansion.
  // We don't reformat (xx-XX casing) since label tables are checked
  // against lowercase keys.
  const parts = lower.split('-').filter(Boolean)
  if (parts.length === 0) return []
  const chain: string[] = []
  for (let i = parts.length; i > 0; i--) {
    chain.push(parts.slice(0, i).join('-'))
  }
  return chain
}
